import { useState } from 'react';
import { push } from 'redux-first-history';
import { useAppDispatch } from '../../../../../store/hooks';
import { logError } from '@zg-rentals/log-error';
import { Box, Divider, Heading, Paragraph } from '@zillow/constellation-10';
import type {
  IdentityVerificationInfo as BackendIdentityVerificationInfo,
  VerifyLandlordIdentityExtendedWebApiRequestDto,
} from '@zg-rentals/ts-rent-payment-client';
import UserActions from '@common/actions/UserActions';
import AppConstants from '@common/constants/AppConstants';
import DebouncedButton from '@common/components/ConstellationDebouncedButton';
import Modal from '@rental-manager/pages/Onboarding/WizardShell/WizardScreen';
import DetailsCard from '@rental-manager/components/DetailsCardConstellationV10';
import { renderDetailRows } from '@rental-manager/components/DetailRows';
import type { DetailRow } from '@rental-manager/components/DetailRows';
import userPersonas from '@common/constants/userPersonas';
import IreneOnboardingCSEvents from '@rental-manager/tracking/OnboardingClickStreamEvents';
import type { ZrmClickstreamListing, ZrmClickstreamUser } from '@zg-rentals/clickstream';
import { DEFAULT_ERROR, TAX_ID_ERROR } from '@rental-manager/constants/NotificationConstants';
import { editTaxVerificationPage } from '@rental-manager/actions/OnboardingActions';
import { createMoveInCosts, updateTenantInfo } from '@rental-manager/actions/requests';
import { SendNotificationWebApiHandler_sendNotification } from '@zg-rentals/ts-rent-payment-client';
import { showModalNotification } from '@common/actions/NotificationActions';
import { useVerifyLandlordIdentity } from '@rental-manager/hooks/useVerifyLandlordIdentity';
import type { MoveInCostsSettingsInterface } from '@rental-manager/pages/Dashboard/types';
import type { MoveInCostsInterface } from '@rental-manager/models/Types';
import paths from '@rental-manager/pathsManifest';
import { getSignInPage } from '@common/paymentsUtils/api';
import HTTP_STATUS from '@common/constants/HttpConstants';
import { DatarouterError } from '@zg-rentals/web-base';

type IdentityVerificationInfo = Omit<BackendIdentityVerificationInfo, 'dobDay' | 'dobMonth' | 'dobYear'> & {
  dobDay: string | number;
  dobMonth: string | number;
  dobYear: string | number;
  selectedTaxVerificationType: string;
};

interface TenantInvitationInfo {
  emailAddress: string;
  tenantFirstName: string;
  tenantLastName: string;
}

interface Props {
  baseMonthlyAmount: Array<string>;
  checkIsPhoneVerificationNeeded: () => Promise<boolean>;
  currentProperty: ZrmClickstreamListing;
  depositAccount: Array<string>;
  editAccountInfo: () => void;
  editDueDate: () => void;
  editIdentityVerification: () => void;
  editMoveInCosts: () => void;
  editPaymentTerm: () => void;
  editRentAmount: () => void;
  editTenantInvitationInfo: () => void;
  getVerificationPayload: () => VerifyLandlordIdentityExtendedWebApiRequestDto | null;
  goToPhoneVerificationPage: () => void;
  hasLandlordOnboarded: boolean;
  identityVerificationInfo: IdentityVerificationInfo;
  listingName: string;
  monthlyPaymentDate: Array<string>;
  moveInCosts: Array<DetailRow>;
  moveInCostsSettings: MoveInCostsSettingsInterface;
  rentTermsKey: string;
  tenantInvitationInfo: TenantInvitationInfo;
  identityCheckResponse: string;
  onBack: () => void;
  onClose: () => void;
  baseUrl: string;
  propertyId: string;
  generateMoveInCostPayload: (settings: MoveInCostsSettingsInterface) => Array<MoveInCostsInterface>;
  paymentTerm: Array<DetailRow>;
  primaryTenant: Array<string>;
  user: ZrmClickstreamUser;
}

const OnboardingConfirmationPage = ({
  baseMonthlyAmount,
  checkIsPhoneVerificationNeeded,
  currentProperty,
  depositAccount,
  editAccountInfo,
  editDueDate,
  editIdentityVerification,
  editMoveInCosts,
  editPaymentTerm,
  editRentAmount,
  editTenantInvitationInfo,
  getVerificationPayload,
  goToPhoneVerificationPage,
  hasLandlordOnboarded,
  identityVerificationInfo,
  listingName = '',
  monthlyPaymentDate,
  moveInCosts: moveInCostsDisplay,
  moveInCostsSettings,
  rentTermsKey,
  tenantInvitationInfo,
  identityCheckResponse,
  onBack: onBackProp,
  onClose: onCloseProp,
  baseUrl,
  propertyId,
  generateMoveInCostPayload,
  paymentTerm,
  primaryTenant,
  user,
}: Props) => {
  const [showLoadingScreen, setShowLoadingScreen] = useState<boolean>(false);
  const dispatch = useAppDispatch();

  const handlePostVerificationFlow = async () => {
    const settings =
      moveInCostsSettings ??
      ({
        securityDepositSettings: {
          securityDepositPaymentAmount: 0,
          securityDepositNotes: '',
          securityDepositStartsOpen: false,
        },
        proratedRentSettings: {
          proratedRentPaymentAmount: 0,
          proratedRentNotes: '',
          proratedRentStartsOpen: false,
        },
        otherSettings: [],
        dueDate: '',
      } as MoveInCostsSettingsInterface);
    const { securityDepositSettings, proratedRentSettings, otherSettings, dueDate } = settings;
    const moveInCosts = generateMoveInCostPayload(settings);

    const moveInCostsPayload = {
      dueDate,
      moveInCosts,
      rentTermsKey,
      isOnboarding: true,
      identityCheckResponse,
    };

    if (
      proratedRentSettings.proratedRentStartsOpen === true ||
      securityDepositSettings.securityDepositStartsOpen === true ||
      otherSettings.length !== 0
    ) {
      await dispatch(createMoveInCosts({ propertyId, ...moveInCostsPayload }));
    }

    const tenant = tenantInvitationInfo ?? {};
    const tenantPayload = {
      rentTermsKey,
      tenantInfo: {
        email: tenant.emailAddress ?? '',
        firstName: tenant.tenantFirstName ?? '',
        lastName: tenant.tenantLastName ?? '',
      },
    };
    await dispatch(updateTenantInfo({ ...tenantPayload, propertyId }));

    void SendNotificationWebApiHandler_sendNotification({
      resourceId: propertyId,
      notificationType: 'rachelPaymentInvite',
    }).catch((error) => {
      logError({ error, context: 'OnboardingConfirmationPage - sendNotification' });
    });

    try {
      await dispatch(
        UserActions.getUser({
          appName: AppConstants.RENTAL_MANAGER,
          pageWithSensitiveInformation: true,
          includeSummary: false,
          brand: 'zillow',
        }),
      );
    } catch {
      logError({ error: new Error(`Failed to fetch user`) });
    }

    dispatch(push(baseUrl + paths.onboarding.postConfirmationPage));
  };

  const { mutate: verifyLandlordIdentity } = useVerifyLandlordIdentity({
    postVerificationFlow: handlePostVerificationFlow,
    onVerifyError: (errors) => {
      if (errors.taxId === 'failed') {
        dispatch(showModalNotification(TAX_ID_ERROR));
        dispatch(editTaxVerificationPage({ propertyId }));
        dispatch(
          push({
            pathname: baseUrl + paths.onboarding.taxVerificationPage,
            state: { fromWhere: 'onboardingReview' },
          }),
        );
        return;
      }
      dispatch(showModalNotification(DEFAULT_ERROR, { subject: 'Identity', verb: 'verified' }));
    },
    onError: () => {
      dispatch(showModalNotification(DEFAULT_ERROR, { subject: 'Identity', verb: 'verified' }));
    },
    onPostVerificationError: (error) => {
      if (DatarouterError.is(error) && error.responseStatus === HTTP_STATUS.SC_UNAUTHORIZED) {
        window.location = getSignInPage();
        return;
      }
      dispatch(showModalNotification(DEFAULT_ERROR, { subject: 'Invite', verb: 'sent' }));
    },
    onSettled: () => setShowLoadingScreen(false),
    context: 'OnboardingConfirmationPage - verifyLandlordIdentity',
  });

  const onSubmit = async (): Promise<void> => {
    IreneOnboardingCSEvents.emitContinueFromConfirmationPageEvent({ currentProperty, user });
    setShowLoadingScreen(true);

    try {
      const payload = getVerificationPayload();
      if (payload) {
        verifyLandlordIdentity({ postData: payload });
      } else {
        // No identity verification needed — run post-verification flow (move-in costs, tenant, redirect)
        try {
          await handlePostVerificationFlow();
        } catch (flowError) {
          if (DatarouterError.is(flowError) && flowError.responseStatus === HTTP_STATUS.SC_UNAUTHORIZED) {
            window.location = getSignInPage();
            return;
          }
          logError({
            error: flowError,
            context: 'OnboardingConfirmationPage - onSubmit (no verification needed)',
          });
          dispatch(showModalNotification(DEFAULT_ERROR, { subject: 'Invite', verb: 'sent' }));
        } finally {
          setShowLoadingScreen(false);
        }
      }
    } catch (error) {
      logError({ error, context: 'OnboardingConfirmationPage - onSubmit' });
      dispatch(showModalNotification(DEFAULT_ERROR, { subject: 'Invite', verb: 'sent' }));
      setShowLoadingScreen(false);
    }
  };

  const onBack = (): void => {
    IreneOnboardingCSEvents.emitBackFromOnboardingFlowEvent({
      currentProperty,
      user,
      triggerObject: 'invite_tenant',
    });
    onBackProp();
  };

  const onClose = (): void => {
    IreneOnboardingCSEvents.emitExitFromOnboardingFlowEvent({
      currentProperty,
      user,
      triggerObject: 'invite_tenant',
    });
    onCloseProp();
  };

  const getTaxVerification = () => {
    const {
      firstName,
      lastName,
      address,
      city,
      state: addressState,
      zip,
      phoneNumber,
      dobMonth,
      dobDay,
      dobYear,
      ssn,
      companyName,
      companyDbaName,
      companyTaxId,
      companyAddress,
      companyCity,
      companyState,
      companyZip,
      selectedTaxVerificationType,
    } = identityVerificationInfo;

    const birthDate = new Date(
      parseInt(String(dobYear), 10),
      parseInt(String(dobMonth), 10) - 1,
      parseInt(String(dobDay), 10),
    );
    const birthDateFormatted = birthDate.toLocaleString('en-us', {
      month: 'long',
      year: 'numeric',
      day: '2-digit',
    });

    const last4DigitSSN = ssn?.slice(-4);
    if (selectedTaxVerificationType === 'single_member_llc') {
      return [
        'Single LLC',
        companyName,
        { label: 'DBA', value: companyDbaName || 'N/A' },
        { label: 'EIN', value: companyTaxId },
        `${firstName} ${lastName}`,
        address,
        `${city}, ${addressState} ${zip}`,
        phoneNumber,
        { label: 'Date of birth', value: birthDateFormatted },
        { label: 'Last 4 digits of SSN', value: `XXX-XX-${last4DigitSSN}` },
      ];
    } else if (selectedTaxVerificationType === 'multi_member_llc') {
      return [
        'Multi LLC or corporation',
        { label: 'Business', value: '' },
        companyName,
        { label: 'DBA', value: companyDbaName || 'N/A' },
        { label: 'EIN', value: companyTaxId },
        companyAddress,
        `${companyCity}, ${companyState} ${companyZip}`,
        { label: 'Owner', value: '' },
        `${firstName} ${lastName}`,
        address,
        `${city}, ${addressState} ${zip}`,
        phoneNumber,
        { label: 'Date of birth', value: birthDateFormatted },
        { label: 'Last 4 digits of SSN', value: `XXX-XX-${last4DigitSSN}` },
      ];
    } else {
      return [
        'Individual',
        `${firstName} ${lastName}`,
        address,
        `${city}, ${addressState} ${zip}`,
        phoneNumber,
        { label: 'Date of birth', value: birthDateFormatted },
        { label: 'Last 4 digits of SSN', value: `XXX-XX-${last4DigitSSN}` },
      ];
    }
  };

  return (
    <Modal
      persona={userPersonas.LANDLORD}
      onRequestClose={onClose}
      header="Payment setup"
      body={
        <Box
          css={{
            display: 'grid',
            gridTemplateRows: 'auto',
            gap: 'default',
          }}
        >
          <Box>
            <Heading level={3} textStyle="body-lg-bold">
              Confirm info and invite tenant
            </Heading>
            <Paragraph>Review the information you provided for {listingName} and confirm it’s correct.</Paragraph>
          </Box>

          <Box>
            <Divider />
            <DetailsCard className="fs-exclude" title="Monthly rent" onEdit={editRentAmount}>
              {baseMonthlyAmount}
            </DetailsCard>
          </Box>

          <Box>
            <Divider />
            <DetailsCard className="fs-exclude" title="Monthly payment date" onEdit={editDueDate}>
              {monthlyPaymentDate}
            </DetailsCard>
          </Box>

          <Box>
            <Divider />
            <DetailsCard className="fs-exclude" title="Payment period" onEdit={editPaymentTerm}>
              {renderDetailRows(paymentTerm)}
            </DetailsCard>
          </Box>

          <Box>
            <Divider />
            <DetailsCard className="fs-exclude" title="Move-in costs" onEdit={editMoveInCosts}>
              {renderDetailRows(moveInCostsDisplay)}
            </DetailsCard>
          </Box>

          <Box>
            <Divider />
            <DetailsCard title="Primary tenant" className="fs-exclude txtWrap" onEdit={editTenantInvitationInfo}>
              {primaryTenant}
            </DetailsCard>
          </Box>

          {!hasLandlordOnboarded && (
            <Box>
              <Divider />
              <DetailsCard className="fs-exclude" title="Tax information" onEdit={editIdentityVerification}>
                {renderDetailRows(getTaxVerification())}
              </DetailsCard>
            </Box>
          )}

          <Box>
            <Divider />
            <DetailsCard
              className="fs-exclude"
              title="Deposit account"
              onEdit={() => {
                checkIsPhoneVerificationNeeded()
                  .then((isPhoneVerificationNeeded) => {
                    if (isPhoneVerificationNeeded) {
                      return goToPhoneVerificationPage();
                    } else {
                      return editAccountInfo();
                    }
                  })
                  .catch((error) => {
                    logError({ error, context: 'OnboardingConfirmationPage - DetailsCard - onEdit' });
                    return dispatch(showModalNotification(DEFAULT_ERROR));
                  });
              }}
            >
              {depositAccount}
            </DetailsCard>
          </Box>
        </Box>
      }
      backAction={{ onClick: onBack }}
      footerAriaLabel="Payment setup confirmation actions"
      footerEnd={
        <DebouncedButton onClick={onSubmit} loading={showLoadingScreen}>
          Confirm and invite tenant
        </DebouncedButton>
      }
    />
  );
};

export default OnboardingConfirmationPage;
